import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import VideoTrack from '../../base/media/components/web/VideoTrack';
import { getRoomName } from '../../base/conference/functions';
import { setAudioInputDeviceAndUpdateSettings, setVideoInputDeviceAndUpdateSettings } from '../../base/devices/actions.web';
import { MEDIA_TYPE } from '../../base/media/constants';
import { getLocalParticipant } from '../../base/participants/functions';
import { getCurrentCameraDeviceId, getCurrentMicDeviceId, getUserSelectedCameraDeviceId } from '../../base/settings/functions.web';
import { updateSettings } from '../../base/settings/actions';
import { getVideoTrackByParticipant, isLocalTrackMuted } from '../../base/tracks/functions.web';
import { areDebugOverlaysEnabled, isInterpreter } from '../../base/user-role/functions';
import { isUnifiedRoomModel, isVriApp } from '../../base/whitelabel/functions';
import { getLocalVisualLanguageRole, inferRemoteRoles } from '../../visual-language-media-policy/functions';
import i18next from '../../base/i18n/i18next';
import CallWaitingOverlay from '../../call-management/components/CallWaitingOverlay';
import IncomingCallOverlay from '../../call-management/components/IncomingCallOverlay';
import RingClientBanner from './RingClientBanner.web';
import { shouldDisplayTileView } from '../../video-layout/functions.web';
import useSelfViewPosition from './useSelfViewPosition.web';
import AudioAutoplayPrompt from './web/AudioAutoplayPrompt.web';
import PeerWaitingPane, { PEER_PANE_FADE_MS } from './web/PeerWaitingPane.web';
import SelfViewDeviceSettings from './web/SelfViewDeviceSettings.web';
import { resolvePeerVideo, resolvePeerWaiting } from '../peerWaiting';
import { selfViewDocksInGrid } from '../stageGrid';
import { readStoredActiveCall } from '../storedCall';
import { handleToggleVideoMuted } from '../../toolbox/actions.any';
import { isToolboxVisible, isVideoMuteButtonDisabled } from '../../toolbox/functions.web';
import { getPersistentItem, hasPersistentAuthToken, setPersistentItem } from '../../vrs-auth/storage';
import { getVisualLanguageMediaEvidence } from '../../visual-language-media-policy/functions';
/**
 * Translate a key to a plain string.
 *
 * `i18next.t` is typed to return `string | object | array | null`, which JSX
 * children and our `string` pane fields both reject. Every key used here is a
 * leaf string, so narrowing once at the boundary keeps the call sites clean.
 */
function tr(key, options) {
    return String(i18next.t(key, options ?? {}));
}
// Your own tile is a mirror thumbnail, so it may crop. Everyone else is shown
// whole: `cover` cut a signer's hands and arms off (half a 16:9 feed in a
// side-by-side column, two thirds on a portrait phone). Module constants keep
// the style identity stable for Video's shouldComponentUpdate.
const LOCAL_VIDEO_STYLE = {
    height: '100%',
    objectFit: 'cover',
    width: '100%'
};
const REMOTE_VIDEO_STYLE = {
    height: '100%',
    objectFit: 'contain',
    width: '100%'
};
const useStyles = makeStyles()(theme => ({
    root: {
        background: '#040404',
        boxSizing: 'border-box',
        display: 'grid',
        gap: theme.spacing(1.5),
        gridAutoRows: 'minmax(0, 1fr)',
        height: '100%',
        minHeight: 0,
        padding: theme.spacing(1.5),
        position: 'relative',
        width: '100%'
    },
    singlePaneRoot: {
        gridTemplateColumns: 'minmax(0, 1fr)'
    },
    twoPaneRoot: {
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        '@media (orientation: portrait)': {
            gridTemplateColumns: '1fr'
        }
    },
    threePaneRoot: {
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        '@media (orientation: portrait)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }
    },
    // Just the video, rounded — same treatment as the native tiles: no card
    // border, blur, or shadow around each person.
    pane: {
        background: '#0b0f16',
        borderRadius: 14,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
        position: 'relative'
    },
    // The device popover may extend past a small self-view tile.
    paneDeviceSettingsOpen: {
        overflow: 'visible'
    },
    // VRS stage: remote participants own the grid; the local tile floats as
    // a picture-in-picture so the person you watch (the signer for the terp,
    // the terp for the Deaf client) gets the screen.
    vrsFocusRootSingle: {
        gridTemplateColumns: 'minmax(0, 1fr)'
    },
    vrsFocusRootDual: {
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        '@media (orientation: portrait)': {
            gridTemplateColumns: '1fr'
        }
    },
    vrsSelfPip: {
        border: `1px solid ${theme.palette.ui04}`,
        aspectRatio: '16 / 10',
        maxHeight: 'calc(100% - 152px)',
        position: 'absolute',
        width: 'clamp(120px, 30%, 260px)',
        maxWidth: '40%',
        zIndex: 6
    },
    moveSelfView: {
        position: 'absolute', top: 0, right: 0, zIndex: 7,
        minWidth: 44, minHeight: 44, border: 0, borderRadius: theme.shape.borderRadius,
        background: theme.palette.ui01, color: theme.palette.text01,
        cursor: 'move', touchAction: 'none', fontSize: 22,
        '&:focus-visible': { outline: `2px solid ${theme.palette.focus01}`, outlineOffset: -2 }
    },
    paneHeader: {
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'space-between',
        padding: theme.spacing(1.5, 1.75, 1)
    },
    paneTitleBlock: {
        minWidth: 0
    },
    paneTitle: {
        color: '#F5F8FB',
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.12em',
        marginBottom: theme.spacing(0.5),
        textTransform: 'uppercase'
    },
    paneDescription: {
        color: 'rgba(226, 236, 247, 0.78)',
        fontSize: 13,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    statusBadge: {
        background: 'rgba(255, 255, 255, 0.08)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: 999,
        color: '#EAF2FA',
        fontSize: 11,
        fontWeight: 600,
        padding: theme.spacing(0.5, 1)
    },
    mediaFrame: {
        background: '#050505',
        borderRadius: 18,
        flex: 1,
        margin: theme.spacing(0, 1.25, 1.25),
        minHeight: 0,
        overflow: 'hidden',
        position: 'relative'
    },
    video: {
        height: '100%',
        objectFit: 'cover',
        // The peer's first frame fades in as the waiting splash fades out.
        transition: `opacity ${PEER_PANE_FADE_MS}ms ease`,
        width: '100%',
        '@media (prefers-reduced-motion: reduce)': {
            transition: 'none'
        }
    },
    // Letterbox bars around a whole remote picture stay stage-dark.
    remoteVideo: {
        background: theme.palette.uiBackground
    },
    videoPending: {
        opacity: 0
    },
    deviceRow: {
        display: 'grid',
        gap: theme.spacing(0.35)
    },
    deviceLabel: {
        color: 'rgba(236, 244, 252, 0.76)',
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: '0.08em',
        textTransform: 'uppercase'
    },
    deviceSelect: {
        background: 'rgba(255, 255, 255, 0.94)',
        border: 0,
        borderRadius: 8,
        color: '#101827',
        font: 'inherit',
        fontSize: 13,
        minHeight: 44,
        minWidth: 0,
        padding: theme.spacing(0.8, 0.9),
        width: '100%',
        '& option': {
            background: '#FFFFFF',
            color: '#101827'
        }
    },
    deviceAction: {
        background: 'rgba(255, 255, 255, 0.94)',
        border: 0,
        borderRadius: 8,
        color: '#101827',
        cursor: 'pointer',
        font: 'inherit',
        fontSize: 13,
        fontWeight: 700,
        minHeight: 44,
        padding: theme.spacing(0.8, 0.9),
        width: '100%',
        '&:disabled': {
            cursor: 'not-allowed',
            opacity: 0.55
        }
    },
    emptyState: {
        alignItems: 'center',
        color: '#F3F6F9',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.25),
        height: '100%',
        justifyContent: 'center',
        padding: theme.spacing(3),
        textAlign: 'center'
    },
    emptyBadge: {
        alignItems: 'center',
        background: 'rgba(255, 255, 255, 0.08)',
        border: '1px solid rgba(255, 255, 255, 0.12)',
        borderRadius: '50%',
        display: 'flex',
        fontSize: 24,
        fontWeight: 700,
        height: 68,
        justifyContent: 'center',
        width: 68
    },
    emptyTitle: {
        fontSize: 18,
        fontWeight: 700
    },
    emptyCopy: {
        color: 'rgba(226, 236, 247, 0.78)',
        fontSize: 14,
        lineHeight: 1.5,
        maxWidth: 280
    },
    participantLabel: {
        backdropFilter: 'blur(6px)',
        background: 'rgba(10, 16, 28, 0.55)',
        borderRadius: 10,
        bottom: 12,
        color: '#F8FBFF',
        left: 12,
        maxWidth: 'calc(100% - 24px)',
        overflow: 'hidden',
        padding: theme.spacing(0.6, 1.25),
        position: 'absolute',
        transition: 'opacity 220ms ease'
    },
    // Rides on the toolbar like the native labels: visible after a click or
    // mouse move, gone when the call is left alone.
    participantLabelHidden: {
        opacity: 0
    },
    participantName: {
        fontSize: 15,
        fontWeight: 600,
        letterSpacing: '0.015em',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    participantMeta: {
        color: 'rgba(233, 241, 247, 0.82)',
        fontSize: 12,
        marginTop: theme.spacing(0.35)
    },
    mediaEvidence: {
        background: 'rgba(5, 10, 18, 0.86)',
        border: '1px solid rgba(162, 213, 255, 0.32)',
        borderRadius: 8,
        bottom: theme.spacing(1.5),
        boxShadow: '0 14px 32px rgba(0, 0, 0, 0.28)',
        color: '#EAF4FF',
        fontSize: 12,
        left: theme.spacing(1.5),
        maxWidth: 'min(430px, calc(100% - 24px))',
        padding: theme.spacing(0.9, 1.1),
        position: 'absolute',
        zIndex: 6,
        [theme.breakpoints.down('sm')]: {
            bottom: theme.spacing(1),
            left: theme.spacing(1),
            right: theme.spacing(1)
        }
    },
    mediaEvidenceSummary: {
        cursor: 'pointer',
        fontWeight: 800,
        outline: 0
    },
    mediaEvidenceGrid: {
        display: 'grid',
        gap: theme.spacing(0.4),
        gridTemplateColumns: 'auto minmax(0, 1fr)',
        marginTop: theme.spacing(0.8)
    },
    mediaEvidenceLabel: {
        color: 'rgba(209, 229, 247, 0.72)',
        fontWeight: 700
    },
    mediaEvidenceValue: {
        color: '#FFFFFF',
        minWidth: 0
    }
}));
function getDeviceLabel(device, fallback, index) {
    return device.label || `${fallback} ${index + 1}`;
}
function persistDevicePreference(kind, deviceId) {
    setPersistentItem(kind === 'camera' ? 'vrs_camera_device_id' : 'vrs_microphone_device_id', deviceId);
}
function persistDeviceLabel(kind, label) {
    if (!label) {
        return;
    }
    setPersistentItem(kind === 'camera' ? 'vrs_camera_device_label' : 'vrs_microphone_device_label', label);
}
function getDevicePreference(kind) {
    const key = kind === 'camera' ? 'vrs_camera_device_id' : 'vrs_microphone_device_id';
    return getPersistentItem(key) || '';
}
function getDeviceLabelPreference(kind) {
    const key = kind === 'camera' ? 'vrs_camera_device_label' : 'vrs_microphone_device_label';
    return getPersistentItem(key) || '';
}
function normalizeCameraLabel(label) {
    return label.replace(/\s#\d*(?!.*\s#\d*)/, '').trim();
}
function findMatchingCameraByLabel(devices, label) {
    const normalizedLabel = normalizeCameraLabel(label);
    return normalizedLabel
        ? devices.find(device => normalizeCameraLabel(device.label || '') === normalizedLabel)
        : undefined;
}
function findPreferredCamera(devices, userSelectedCameraDeviceId) {
    if (userSelectedCameraDeviceId && devices.some(device => device.deviceId === userSelectedCameraDeviceId)) {
        return userSelectedCameraDeviceId;
    }
    const storedCameraId = getDevicePreference('camera');
    if (storedCameraId && devices.some(device => device.deviceId === storedCameraId)) {
        return storedCameraId;
    }
    const storedCameraLabel = getDeviceLabelPreference('camera');
    const labelMatch = findMatchingCameraByLabel(devices, storedCameraLabel);
    if (labelMatch?.deviceId) {
        return labelMatch.deviceId;
    }
    return '';
}
function isVriHostname() {
    if (typeof window === 'undefined') {
        return false;
    }
    const hostname = window.location.hostname.toLowerCase();
    return hostname.startsWith('vri.')
        || hostname.startsWith('staging-vri.')
        || hostname.includes('maplecomm.ca');
}
function isVriConferenceContext(roomName) {
    return isVriApp()
        || isVriHostname()
        || Boolean(roomName?.startsWith('vri-') || roomName?.startsWith('instant-'));
}
function getStoredVrsRole() {
    if (typeof window !== 'undefined') {
        const role = new URLSearchParams(window.location.search).get('role');
        if (role === 'client' || role === 'interpreter') {
            return role;
        }
    }
    const role = getPersistentItem('vrs_user_role');
    if (role === 'client' || role === 'interpreter') {
        return role;
    }
    if (isVriApp()) {
        return isInterpreter() ? 'interpreter' : 'client';
    }
    return undefined;
}
function getStoredTargetClient() {
    const value = getPersistentItem('vrs_target_client');
    return value || undefined;
}
function getParticipantName(participant, fallback = 'Waiting to Join') {
    return participant?.name || participant?.displayName || fallback;
}
function isVisibleRoomParticipant(participant) {
    return !participant.fakeParticipant
        && !participant.isJigasi
        && !participant.botType;
}
function isRenderableVideoTrack(videoTrack) {
    if (!videoTrack) {
        return false;
    }
    const jitsiTrackMuted = typeof videoTrack.jitsiTrack?.isMuted === 'function'
        ? videoTrack.jitsiTrack.isMuted()
        : false;
    return !videoTrack.muted
        && !jitsiTrackMuted
        && videoTrack.isReceivingData !== false;
}
function getPaneStatusText(participant, videoTrack, awaitingText) {
    if (!participant) {
        return awaitingText;
    }
    return isRenderableVideoTrack(videoTrack) ? 'Live' : 'Camera off';
}
function findParticipantByHint(participants, hint) {
    if (!hint) {
        return undefined;
    }
    const normalizedHint = hint.trim().toLowerCase();
    return participants.find(participant => {
        const candidateNames = [
            participant.id,
            participant.name,
            participant.displayName
        ]
            .filter(Boolean)
            .map(value => value.toLowerCase());
        return candidateNames.some(value => value === normalizedHint || value.includes(normalizedHint));
    });
}
function withoutParticipant(participants, participant) {
    if (!participant) {
        return participants;
    }
    return participants.filter(({ id }) => id !== participant.id);
}
export function isVrsSession(roomName) {
    if (getStoredVrsRole()) {
        return true;
    }
    if (getStoredTargetClient()) {
        return true;
    }
    return Boolean(roomName?.startsWith('vrs-'));
}
function getPaneEmptyMessage(role, hasParticipant) {
    if (hasParticipant) {
        return tr('vrs.layout.joinedNoCamera');
    }
    if (role === 'extra') {
        return tr('vrs.layout.extraEmpty');
    }
    if (role === 'interpreter') {
        return tr('vrs.layout.interpreterEmpty');
    }
    if (role === 'client') {
        return tr('vrs.layout.clientEmpty');
    }
    return tr(isUnifiedRoomModel() ? 'vrs.layout.participantEmpty' : 'vrs.layout.hearingEmpty');
}
function getTrackIdentity(videoTrack) {
    return videoTrack?.jitsiTrack || videoTrack;
}
/**
 * The partner's camera for their pane's stage line (peerWaiting.ts
 * resolvePeerVideo): whether their first frame has played, their camera
 * track and its mute, and the camera sources their endpoint signalled. A
 * partner who joined with the camera off has no track at all, so without a
 * track or a source nothing is claimed about their video.
 */
function getPeerVideo(pane, rendered) {
    if (!pane.participant) {
        return 'none';
    }
    const track = pane.videoTrack;
    const jitsiMuted = typeof track?.jitsiTrack?.isMuted === 'function' ? track.jitsiTrack.isMuted() : false;
    return resolvePeerVideo({
        cameraSources: pane.participant.sources?.get(MEDIA_TYPE.VIDEO)?.values(),
        rendered,
        track: track ? { muted: Boolean(track.muted || jitsiMuted) } : undefined
    });
}
const VRSLayout = ({ _hasAccountContext, _isObserver, _localVrsRole, _audioInputDevices, _toolboxVisible, _currentCameraDeviceId, _currentMicDeviceId, _hasLocalVideoTrack, _mediaEvidence, _panes, _roomName, _tileViewEnabled, _userSelectedCameraDeviceId, _videoMuteDisabled, _videoMuted, _videoInputDevices, dispatch }) => {
    const { classes, cx } = useStyles();
    const selfView = useSelfViewPosition();
    const [readyTracks, setReadyTracks] = useState(() => new Set());
    const [deviceSettingsOpen, setDeviceSettingsOpen] = useState(false);
    // Diagnostic chrome (pane headers, Live badges, media health) is a debug
    // tool: hidden unless the account has debugOverlays, or an admin adds
    // ?vrsDebug=1.
    const debugOverlays = _hasAccountContext && areDebugOverlaysEnabled();
    const visiblePanes = useMemo(() => _panes.filter(pane => {
        if (!pane.participant) {
            // The call partner's pane is held open while they are on the
            // way, so the self-view is laid out where it will stay.
            return Boolean(pane.peer);
        }
        // Occupied panes stay visible even without a renderable video track:
        // a camera-off joiner (or phone-only hearing party) used to vanish,
        // so nobody could tell they were connected at all.
        return true;
    }), [_roomName, _panes]);
    const localVriPane = visiblePanes.find(pane => pane.participant?.local);
    const remotePanes = visiblePanes.filter(pane => !pane.participant?.local);
    // The toolbar and custom stage share the same selector: equal tiles when
    // enabled; otherwise remote video with a movable self-view inset.
    // In a large group call the self-view takes the last grid cell instead
    // of floating over a signer (stageGrid.ts).
    const selfViewDocked = !_tileViewEnabled && Boolean(localVriPane) && selfViewDocksInGrid(remotePanes.length);
    const vrsPipMode = !_tileViewEnabled && Boolean(localVriPane) && remotePanes.length >= 1 && !selfViewDocked;
    const orderedPanes = (vrsPipMode || selfViewDocked) && localVriPane
        ? [...remotePanes, localVriPane] : visiblePanes;
    const cameraSelectValue = _videoInputDevices.some(device => device.deviceId === _currentCameraDeviceId)
        ? _currentCameraDeviceId
        : '';
    const micSelectValue = _audioInputDevices.some(device => device.deviceId === _currentMicDeviceId)
        ? _currentMicDeviceId
        : '';
    const onCameraChange = (event) => {
        const deviceId = event.target.value;
        const deviceLabel = _videoInputDevices.find(device => device.deviceId === deviceId)?.label;
        persistDevicePreference('camera', deviceId);
        persistDeviceLabel('camera', deviceLabel);
        if (_hasLocalVideoTrack) {
            dispatch(setVideoInputDeviceAndUpdateSettings(deviceId));
        }
        else {
            dispatch(updateSettings({
                userSelectedCameraDeviceId: deviceId,
                userSelectedCameraDeviceLabel: deviceLabel
            }));
        }
    };
    const onMicChange = (event) => {
        const deviceId = event.target.value;
        const deviceLabel = _audioInputDevices.find(device => device.deviceId === deviceId)?.label;
        persistDevicePreference('microphone', deviceId);
        persistDeviceLabel('microphone', deviceLabel);
        dispatch(setAudioInputDeviceAndUpdateSettings(deviceId));
    };
    const onToggleCamera = () => {
        dispatch(handleToggleVideoMuted(!_videoMuted, true, true));
    };
    useEffect(() => {
        if (_isObserver) {
            return;
        }
        const preferredCameraId = findPreferredCamera(_videoInputDevices, _userSelectedCameraDeviceId);
        if (preferredCameraId && preferredCameraId !== _currentCameraDeviceId && !_hasLocalVideoTrack) {
            const deviceLabel = _videoInputDevices.find(device => device.deviceId === preferredCameraId)?.label;
            persistDevicePreference('camera', preferredCameraId);
            persistDeviceLabel('camera', deviceLabel);
            dispatch(updateSettings({
                userSelectedCameraDeviceId: preferredCameraId,
                userSelectedCameraDeviceLabel: deviceLabel
            }));
        }
    }, [_isObserver, _currentCameraDeviceId, _hasLocalVideoTrack, _userSelectedCameraDeviceId, _videoInputDevices, dispatch]);
    const markTrackReady = useCallback((videoTrack) => {
        const trackIdentity = getTrackIdentity(videoTrack);
        if (!trackIdentity) {
            return;
        }
        setReadyTracks(current => {
            if (current.has(trackIdentity)) {
                return current;
            }
            const next = new Set(current);
            next.add(trackIdentity);
            return next;
        });
    }, []);
    if (!visiblePanes.length) {
        return null;
    }
    return (React.createElement("div", { className: cx(classes.root, vrsPipMode && remotePanes.length === 1 && classes.vrsFocusRootSingle, vrsPipMode && remotePanes.length >= 2 && classes.vrsFocusRootDual, !vrsPipMode && visiblePanes.length === 1 && classes.singlePaneRoot, !vrsPipMode && visiblePanes.length === 2 && classes.twoPaneRoot, !vrsPipMode && visiblePanes.length >= 3 && classes.threePaneRoot), "data-view": _tileViewEnabled ? 'split' : 'focus', id: 'vrs-layout-root', ref: selfView.stage, style: vrsPipMode && remotePanes.length >= 2
            ? { gridTemplateColumns: `repeat(${Math.min(remotePanes.length, 3)}, minmax(0, 1fr))` }
            : undefined },
        _hasAccountContext && React.createElement(CallWaitingOverlay, null),
        _hasAccountContext && React.createElement(IncomingCallOverlay, null),
        React.createElement(AudioAutoplayPrompt, { role: _localVrsRole || (_isObserver ? 'observer' : 'guest') }),
        _localVrsRole === 'interpreter' && !isVriConferenceContext(_roomName) && (React.createElement(RingClientBanner, { clientMissing: !_panes.some(pane => pane.role === 'client' && pane.participant) })),
        orderedPanes.map(pane => {
            const participantName = getParticipantName(pane.participant, pane.title);
            const emptyMessage = getPaneEmptyMessage(pane.role, Boolean(pane.participant));
            const shouldRenderVideo = isRenderableVideoTrack(pane.videoTrack);
            const trackIdentity = getTrackIdentity(pane.videoTrack);
            const isVideoReady = Boolean(pane.videoTrack?.videoStarted || readyTracks.has(trackIdentity));
            const shouldShowDevicePanel = pane.participant?.local
                && (pane.role === 'client' || pane.role === 'interpreter');
            const markCurrentTrackReady = () => markTrackReady(pane.videoTrack);
            // The partner's pane keeps one key from waiting to live, so
            // their video replaces the splash in place.
            return (React.createElement("section", { className: cx(classes.pane, vrsPipMode && pane.participant?.local && classes.vrsSelfPip, shouldShowDevicePanel && deviceSettingsOpen && classes.paneDeviceSettingsOpen), "data-self-view": Boolean(pane.participant?.local), key: pane.peer ? `peer-${pane.role}` : pane.participant?.id || pane.role, ref: vrsPipMode && pane.participant?.local ? selfView.tile : undefined, style: vrsPipMode && pane.participant?.local ? selfView.style : undefined },
                vrsPipMode && pane.participant?.local && (React.createElement("button", { ...selfView.handle, className: classes.moveSelfView, type: 'button' },
                    React.createElement("span", { "aria-hidden": 'true' }, "\u2194"))),
                shouldShowDevicePanel && (React.createElement(SelfViewDeviceSettings, { label: tr('vrs.layout.deviceSettings'), onOpenChange: setDeviceSettingsOpen, open: deviceSettingsOpen, stageRef: selfView.stage },
                    React.createElement("button", { className: classes.deviceAction, disabled: _videoMuteDisabled, onClick: onToggleCamera, type: 'button' }, _videoMuted
                        ? tr('vrs.layout.startCamera')
                        : tr('vrs.layout.stopCamera')),
                    React.createElement("label", { className: classes.deviceRow },
                        React.createElement("span", { className: classes.deviceLabel }, tr('vrs.layout.camera')),
                        React.createElement("select", { className: classes.deviceSelect, onChange: onCameraChange, value: cameraSelectValue },
                            React.createElement("option", { value: '' }, tr('vrs.layout.defaultCamera')),
                            _videoInputDevices.map((device, index) => (React.createElement("option", { key: device.deviceId, value: device.deviceId }, getDeviceLabel(device, tr('vrs.layout.camera'), index)))))),
                    React.createElement("label", { className: classes.deviceRow },
                        React.createElement("span", { className: classes.deviceLabel }, tr('vrs.layout.mic')),
                        React.createElement("select", { className: classes.deviceSelect, onChange: onMicChange, value: micSelectValue },
                            React.createElement("option", { value: '' }, tr('vrs.layout.defaultMic')),
                            _audioInputDevices.map((device, index) => (React.createElement("option", { key: device.deviceId, value: device.deviceId }, getDeviceLabel(device, 'Mic', index)))))))),
                debugOverlays && (React.createElement("div", { className: classes.paneHeader },
                    React.createElement("div", { className: classes.paneTitleBlock },
                        React.createElement("div", { className: classes.paneTitle }, pane.title),
                        React.createElement("div", { className: classes.paneDescription }, pane.description)),
                    React.createElement("div", { className: classes.statusBadge }, pane.statusText))),
                React.createElement("div", { className: classes.mediaFrame },
                    shouldRenderVideo && (React.createElement(React.Fragment, null,
                        React.createElement(VideoTrack, { className: cx(classes.video, !pane.participant?.local && classes.remoteVideo, !isVideoReady && classes.videoPending), eventHandlers: {
                                onCanPlay: markCurrentTrackReady,
                                onLoadedData: markCurrentTrackReady,
                                onPlaying: markCurrentTrackReady
                            }, id: pane.role === 'extra' ? `vrs-extra-${pane.participant?.id}-video` : `vrs-${pane.role}-video`, disablePictureInPicture: true, muted: pane.participant?.local ? undefined : true, style: pane.participant?.local ? LOCAL_VIDEO_STYLE : REMOTE_VIDEO_STYLE, videoTrack: pane.videoTrack }),
                        isVideoReady && (React.createElement(React.Fragment, null,
                            React.createElement("div", { className: cx(classes.participantLabel, !_toolboxVisible && classes.participantLabelHidden) },
                                React.createElement("div", { className: classes.participantName }, participantName),
                                debugOverlays && (React.createElement("div", { className: classes.participantMeta }, pane.description))))))),
                    (!shouldRenderVideo || !isVideoReady) && (!pane.peer || isVideoReady) && (React.createElement("div", { className: classes.emptyState },
                        React.createElement("div", { className: classes.emptyBadge }, pane.title.charAt(0)),
                        React.createElement("div", { className: classes.emptyTitle }, participantName),
                        React.createElement("div", { className: classes.emptyCopy }, emptyMessage))),
                    pane.peer && (React.createElement(PeerWaitingPane, { peer: pane.peer, peerId: pane.participant?.id, peerName: pane.participant ? getParticipantName(pane.participant, '') : undefined, peerPresent: Boolean(pane.participant), video: getPeerVideo(pane, shouldRenderVideo && isVideoReady) })))));
        }),
        _mediaEvidence && debugOverlays && (React.createElement("details", { className: classes.mediaEvidence },
            React.createElement("summary", { className: classes.mediaEvidenceSummary }, `Media health: ${_mediaEvidence.mode}`),
            React.createElement("div", { className: classes.mediaEvidenceGrid },
                React.createElement("span", { className: classes.mediaEvidenceLabel }, "Room"),
                React.createElement("span", { className: classes.mediaEvidenceValue }, `${_mediaEvidence.roomKind.toUpperCase()} - ${_mediaEvidence.participantCount} participants`),
                React.createElement("span", { className: classes.mediaEvidenceLabel }, "Transport"),
                React.createElement("span", { className: classes.mediaEvidenceValue }, _mediaEvidence.p2pActive
                    ? 'P2P active - investigate'
                    : _mediaEvidence.jvbExpected ? 'JVB/SFU expected' : 'Direct media acceptable'),
                React.createElement("span", { className: classes.mediaEvidenceLabel }, "Priority"),
                React.createElement("span", { className: classes.mediaEvidenceValue }, _mediaEvidence.priorityRoles.join(', ') || 'none'),
                React.createElement("span", { className: classes.mediaEvidenceLabel }, "Video"),
                React.createElement("span", { className: classes.mediaEvidenceValue }, `${_mediaEvidence.visibleVideoTrackCount}/${_mediaEvidence.remoteVideoTrackCount} remote tracks visible`),
                React.createElement("span", { className: classes.mediaEvidenceLabel }, "Lower priority"),
                React.createElement("span", { className: classes.mediaEvidenceValue }, _mediaEvidence.hiddenByPolicyCount),
                _mediaEvidence.missingVideoParticipantNames.length > 0 && (React.createElement(React.Fragment, null,
                    React.createElement("span", { className: classes.mediaEvidenceLabel }, "Missing video"),
                    React.createElement("span", { className: classes.mediaEvidenceValue }, _mediaEvidence.missingVideoParticipantNames.join(', ')))))))));
};
function _mapStateToProps(state) {
    const roomRole = state['features/base/jwt']?.user?.role;
    const isObserver = roomRole === 'observer';
    const hasAccountContext = hasPersistentAuthToken() && roomRole !== 'guest' && !isObserver;
    // Observers receive the participant stage without an empty self-camera pane.
    const localParticipant = isObserver ? undefined : getLocalParticipant(state);
    const remoteParticipants = Array.from(state['features/base/participants'].remote.values())
        .filter(isVisibleRoomParticipant);
    const localRole = hasAccountContext ? getStoredVrsRole() : undefined;
    const targetClient = hasAccountContext ? getStoredTargetClient() : undefined;
    const roomName = getRoomName(state);
    const isVriContext = isVriConferenceContext(roomName);
    // Who the local person is waiting for, named from the stored call for
    // this room. Guests and observers get no partner pane. The stored call is
    // read without writing anything back (this runs on every store update).
    const peer = resolvePeerWaiting({
        localRole,
        roomName,
        storedCall: localRole ? readStoredActiveCall() : undefined
    });
    let clientParticipant;
    let interpreterParticipant;
    let hearingParticipant;
    let remainingParticipants = remoteParticipants.slice();
    /*
     * Pane roles bind by the malkaVisualRole each endpoint announces at join
     * (visual-language-media-policy), falling back to the historical
     * positional guess only for peers that never announce (older mobile
     * clients). Guessing order mattered before announcing existed: a guest
     * who joined first stole the interpreter's pane and its blue border.
     */
    const roleByParticipantId = inferRemoteRoles(remoteParticipants, hasAccountContext ? getLocalVisualLanguageRole() : undefined, targetClient);
    const remoteWithRole = (role) => remainingParticipants.find(participant => roleByParticipantId[participant.id] === role);
    if (localRole === 'interpreter') {
        interpreterParticipant = localParticipant;
        clientParticipant = remoteWithRole('client')
            || findParticipantByHint(remainingParticipants, targetClient)
            || remainingParticipants[0];
        remainingParticipants = withoutParticipant(remainingParticipants, clientParticipant);
        if (!isVriContext) {
            hearingParticipant = remainingParticipants[0];
            remainingParticipants = withoutParticipant(remainingParticipants, hearingParticipant);
        }
    }
    else if (localRole === 'client') {
        clientParticipant = localParticipant;
        interpreterParticipant = remoteWithRole('interpreter') || remainingParticipants[0];
        remainingParticipants = withoutParticipant(remainingParticipants, interpreterParticipant);
        if (!isVriContext) {
            hearingParticipant = remainingParticipants[0];
            remainingParticipants = withoutParticipant(remainingParticipants, hearingParticipant);
        }
    }
    else {
        hearingParticipant = isVriContext ? undefined : localParticipant;
        interpreterParticipant = remoteWithRole('interpreter') || remainingParticipants[0];
        remainingParticipants = withoutParticipant(remainingParticipants, interpreterParticipant);
        clientParticipant = remoteWithRole('client')
            || findParticipantByHint(remainingParticipants, targetClient)
            || remainingParticipants[0];
        remainingParticipants = withoutParticipant(remainingParticipants, clientParticipant);
    }
    // A phone/PSTN leg arrives as a jigasi participant and the room-wide
    // filter above drops it entirely — leaving deaf clients with zero visual
    // evidence that the hearing party connected, rang, or sat on hold on the
    // one channel they cannot verify by ear. Surface it as the hearing pane.
    if (!hearingParticipant) {
        hearingParticipant = Array.from(state['features/base/participants'].remote.values())
            .find(participant => participant.isJigasi && !participant.fakeParticipant);
    }
    const clientVideoTrack = getVideoTrackByParticipant(state, clientParticipant);
    const interpreterVideoTrack = getVideoTrackByParticipant(state, interpreterParticipant);
    const hearingVideoTrack = getVideoTrackByParticipant(state, hearingParticipant);
    // Relay tenants label the third pane "Hearing Party" because that party is
    // expected on a phone leg. Unified tenants (MalkaVCS) put both parties in
    // the room as equals, so the relay framing would be wrong there.
    const unifiedRoom = isUnifiedRoomModel();
    const thirdPaneKey = unifiedRoom ? 'participant' : 'hearing';
    return {
        _hasAccountContext: hasAccountContext,
        _isObserver: isObserver,
        _localVrsRole: localRole,
        _audioInputDevices: state['features/base/devices'].availableDevices.audioInput || [],
        _currentCameraDeviceId: getCurrentCameraDeviceId(state),
        _currentMicDeviceId: getCurrentMicDeviceId(state),
        _hasLocalVideoTrack: Boolean(getVideoTrackByParticipant(state, localParticipant)),
        _mediaEvidence: getVisualLanguageMediaEvidence(state),
        _roomName: roomName,
        _toolboxVisible: isToolboxVisible(state),
        _tileViewEnabled: shouldDisplayTileView(state),
        _userSelectedCameraDeviceId: getUserSelectedCameraDeviceId(state) || '',
        _videoMuteDisabled: isVideoMuteButtonDisabled(state),
        _videoMuted: isLocalTrackMuted(state['features/base/tracks'], MEDIA_TYPE.VIDEO),
        _videoInputDevices: state['features/base/devices'].availableDevices.videoInput || [],
        _panes: [
            {
                description: tr('vrs.layout.clientDescription'),
                participant: clientParticipant,
                peer: peer?.paneRole === 'client' ? peer : undefined,
                role: 'client',
                statusText: getPaneStatusText(clientParticipant, clientVideoTrack, tr('vrs.layout.awaitingClient')),
                title: tr('vrs.layout.clientTitle'),
                videoTrack: clientVideoTrack
            },
            {
                description: tr('vrs.layout.interpreterDescription'),
                participant: interpreterParticipant,
                peer: peer?.paneRole === 'interpreter' ? peer : undefined,
                role: 'interpreter',
                statusText: getPaneStatusText(interpreterParticipant, interpreterVideoTrack, tr('vrs.layout.awaitingInterpreter')),
                title: tr('vrs.layout.interpreterTitle'),
                videoTrack: interpreterVideoTrack
            },
            {
                description: tr(`vrs.layout.${thirdPaneKey}Description`),
                participant: hearingParticipant,
                role: 'hearing',
                statusText: getPaneStatusText(hearingParticipant, hearingVideoTrack, tr(unifiedRoom ? 'vrs.layout.awaitingParticipant' : 'vrs.layout.awaitingHearing')),
                title: tr(`vrs.layout.${thirdPaneKey}Title`),
                videoTrack: hearingVideoTrack
            },
            // Everyone beyond the three named roles still deserves a video
            // tile — the interpreter especially must be able to see all
            // participants in a group call, not a list of nameplates.
            ...remainingParticipants.map(participant => {
                const videoTrack = getVideoTrackByParticipant(state, participant);
                return {
                    description: tr('vrs.layout.extraTitle'),
                    participant,
                    role: 'extra',
                    statusText: getPaneStatusText(participant, videoTrack, tr('vrs.layout.joining')),
                    title: getParticipantName(participant, tr('vrs.layout.extraTitle')),
                    videoTrack
                };
            })
        ]
    };
}
export default connect(_mapStateToProps)(VRSLayout);
